import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import {
  fetchPersoonsdocumenten,
  updatePersoonsdocument,
  deletePersoonsdocument,
  openPersoonsdocument,
  type Persoonsdocument,
  type PersoonsdocumentType,
  type PersoonsdocumentenData,
} from '@/api/persoonsdocumenten';
import { formatDate } from './LimosaBadge';
import { PersoonsdocumentUpload } from './PersoonsdocumentUpload';

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError && err.message ? err.message : fallback);

const STATUS: Record<PersoonsdocumentType['status'], { label: string; className: string }> = {
  aanwezig: { label: 'In orde', className: 'bg-green-100 text-green-800' },
  ontbreekt: { label: 'Ontbreekt', className: 'bg-red-100 text-red-800' },
  verlopen: { label: 'Verlopen', className: 'bg-orange-100 text-orange-800' },
  niet_nodig: { label: 'Niet nodig', className: 'bg-gray-100 text-td-muted' },
};

const VERPLICHT: Record<PersoonsdocumentType['verplichtVoor'], string> = {
  iedereen: 'verplicht voor iedereen op de werf',
  limosa: 'verplicht als Limosa verplicht is',
  niemand: 'optioneel',
};

/** The person a dialog is about. */
export interface DocumentenPerson {
  id: number;
  name: string;
}

function DocumentItem({ partnerId, type, doc, binDays, onChanged }: {
  partnerId: number;
  type: PersoonsdocumentType;
  doc: Persoonsdocument;
  /** Odoo's bin delay, for the wording. */
  binDays: number;
  onChanged: () => void;
}) {
  const [tot, setTot] = useState(doc.geldigTot ?? '');
  const [opmerking, setOpmerking] = useState(doc.opmerking ?? '');
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = (tot !== (doc.geldigTot ?? '') && tot !== '') || opmerking.trim() !== (doc.opmerking ?? '');

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    return run(() => updatePersoonsdocument(doc.id, {
      ...(tot && tot !== doc.geldigTot ? { geldigTot: tot } : {}),
      ...(opmerking.trim() !== (doc.opmerking ?? '') ? { opmerking: opmerking.trim() } : {}),
    }), 'Document bijwerken mislukt');
  };
  const open = () => openPersoonsdocument(doc.id).catch((err) => setError(errorText(err, 'Document openen mislukt')));
  const button = 'px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-60';

  return (
    <li className={`border rounded-lg p-3 text-sm ${doc.gearchiveerd ? 'border-gray-100 bg-gray-50 text-td-muted' : 'border-gray-200'}`} data-testid="persoonsdocument">
      {doc.gearchiveerd ? (
        <p>
          In de prullenbak van Odoo{doc.geldigTot ? ` · geldig tot ${formatDate(doc.geldigTot)}` : ''}
          <span className="block text-xs">Wordt na {binDays} dagen definitief verwijderd; tot dan kan je het terugzetten.</span>
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-td-muted">
            Geldig tot
            <input type="date" value={tot} onChange={(e) => setTot(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
          <label className="col-span-2 text-xs text-td-muted">
            Opmerking
            <input type="text" maxLength={500} value={opmerking} onChange={(e) => setOpmerking(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
        </div>
      )}
      {!doc.gearchiveerd && doc.meerdereTypes && <p className="text-xs text-orange-700 mt-1">Dit document heeft in Odoo meer dan één documenttype: het telt pas mee als er één overblijft.</p>}
      {!doc.gearchiveerd && !doc.geldigTot && <p className="text-xs text-orange-700 mt-1">Nog geen geldigheidsdatum: dit document telt pas mee als die ingevuld is.</p>}
      {doc.uploadedBy && <p className="text-xs text-td-light mt-1">Opgeladen door {doc.uploadedBy}</p>}

      <div className="mt-2 flex flex-wrap gap-2">
        {doc.hasDocument && (
          <button type="button" onClick={open} className={`${button} text-td-blue border border-td-blue hover:bg-td-blue/10`}>Openen</button>
        )}
        {!doc.gearchiveerd && (
          <>
            <button type="button" disabled={busy || !changed} onClick={() => void save()} className={`${button} text-white bg-green-600`}>
              {busy ? 'Bezig…' : 'Opslaan'}
            </button>
            <button type="button" disabled={busy} onClick={() => setReplacing(true)} className={`${button} text-td-blue border border-td-blue`}>Vervangen</button>
          </>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(() => updatePersoonsdocument(doc.id, { gearchiveerd: !doc.gearchiveerd }), 'Document bijwerken mislukt')}
          className={`${button} text-td-muted border border-gray-300`}
        >
          {doc.gearchiveerd ? 'Terugzetten' : 'Archiveren'}
        </button>
        {confirmDelete ? (
          <>
            <button type="button" disabled={busy} onClick={() => void run(() => deletePersoonsdocument(doc.id), 'Document verwijderen mislukt')} className={`${button} text-white bg-red-600`}>
              Definitief verwijderen
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmDelete(false)} className={`${button} text-td-muted border border-gray-300`}>Toch niet</button>
          </>
        ) : (
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)} className={`${button} text-red-700 border border-red-300`}>Verwijderen</button>
        )}
      </div>
      {confirmDelete && <p className="mt-1 text-xs text-red-700">Het bestand wordt echt verwijderd, niet gearchiveerd. Dit kan niet ongedaan gemaakt worden.</p>}
      {replacing && (
        <PersoonsdocumentUpload
          partnerId={partnerId}
          typeId={type.id}
          typeName={type.name}
          vervangt={doc.id}
          onStored={onChanged}
          onCancel={() => setReplacing(false)}
          onUploaded={() => {
            setReplacing(false);
            onChanged();
          }}
        />
      )}
      {error && <p className="mt-2 text-xs text-red-600" role="alert">{error}</p>}
    </li>
  );
}

function TypeSection({ partnerId, type, records, binDays, onChanged }: {
  partnerId: number;
  type: PersoonsdocumentType;
  records: Persoonsdocument[];
  binDays: number;
  onChanged: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const active = records.filter((r) => !r.gearchiveerd);
  const archived = records.filter((r) => r.gearchiveerd);
  const status = STATUS[type.status];

  return (
    <section className="mt-4" aria-label={type.name} data-testid="persoonsdocument-type">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-td-navy">
          {type.name}
          <span className={`ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.className}`}>{status.label}</span>
        </h3>
        {!uploading && (
          <button type="button" onClick={() => setUploading(true)} className="px-3 py-1.5 text-xs font-semibold text-td-blue border border-td-blue rounded-lg hover:bg-td-blue/10">
            Uploaden
          </button>
        )}
      </div>
      <p className="text-[11px] text-td-muted">{VERPLICHT[type.verplichtVoor]}{type.verplichtVoor === 'limosa' && (type.required ? ' — deze persoon wel' : ' — deze persoon niet')}</p>
      {uploading && (
        <PersoonsdocumentUpload
          partnerId={partnerId}
          typeId={type.id}
          typeName={type.name}
          onCancel={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            onChanged();
          }}
        />
      )}
      {active.length > 0 && (
        <ul className="mt-2 space-y-2">
          {active.map((d) => <DocumentItem key={`${d.id}-${d.geldigTot ?? ''}-${d.opmerking ?? ''}`} partnerId={partnerId} type={type} doc={d} binDays={binDays} onChanged={onChanged} />)}
        </ul>
      )}
      {archived.length > 0 && (
        <>
          <button type="button" onClick={() => setShowArchived((v) => !v)} className="mt-2 text-xs text-td-muted underline">
            {showArchived ? 'Prullenbak verbergen' : `In de prullenbak (${archived.length})`}
          </button>
          {showArchived && (
            <ul className="mt-2 space-y-2">
              {archived.map((d) => <DocumentItem key={d.id} partnerId={partnerId} type={type} doc={d} binDays={binDays} onChanged={onChanged} />)}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/**
 * A person's documents per type the office keeps in Odoo (A1, VCA,
 * Identiteitsbewijs, …): whether each is needed and in order today, and per
 * document: open, change validity, replace, archive, delete for good.
 */
export function PersoonsdocumentenDialog({ person, onClose }: { person: DocumentenPerson; onClose: () => void }) {
  const [data, setData] = useState<PersoonsdocumentenData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchPersoonsdocumenten(person.id)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => setError(errorText(err, 'Documenten ophalen mislukt')));
  }, [person.id]);

  useEffect(load, [load]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="documenten-dialog-title"
        className="bg-white w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="documenten-dialog-title" className="text-lg font-extrabold text-td-navy">Documenten — {person.name}</h2>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="text-td-muted hover:text-td-navy text-xl leading-none">×</button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
        {!data && !error && <p className="mt-3 text-sm text-td-muted">Laden…</p>}
        {data && data.types.length === 0 && (
          <p className="mt-3 text-sm text-td-muted">Er zijn nog geen documenttypes. Vul in Odoo bij Documenten › Configuratie › Tags "Verplicht voor (persoonsdocument)" in op een tag.</p>
        )}
        {data?.types.map((t) => (
          <TypeSection key={t.id} partnerId={person.id} type={t} records={data.records.filter((r) => r.typeId === t.id)} binDays={data.binDays ?? 30} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
